import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AdminAuthContext = createContext();

export const useAdminAuth = () => useContext(AdminAuthContext);

export const AdminAuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if admin is logged in on initial load
  useEffect(() => {
    const checkAdminAuth = async () => {
      const token = localStorage.getItem('adminToken');
      
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        // Configure axios with the token
        const config = {
          headers: {
            Authorization: `Bearer ${token}`
          }
        };

        // Fetch current admin user data
        const response = await axios.get('/admin/me', config);
        setAdmin(response.data);
        setError(null);
      } catch (err) {
        console.error('Admin authentication error:', err);
        if (err.response && err.response.status === 401) {
          localStorage.removeItem('adminToken');
          setError('Authentication failed. Please login again.');
        } else {
          setError('Network/server error. Please check your connection.');
        }
      } finally {
        setLoading(false);
      }
    };

    checkAdminAuth();
  }, []);

  // Admin login function
  const login = async (username, password) => {
    setLoading(true);
    setError(null);
    
    try {
      // Create form data with OAuth2 password flow parameters
      const formData = new URLSearchParams();
      formData.append('grant_type', 'password');
      formData.append('username', username);
      formData.append('password', password);
      formData.append('scope', '');
      formData.append('client_id', 'string');
      formData.append('client_secret', 'string');
      
      // Send request with form-urlencoded content type
      const response = await axios.post('/admin/login', formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      });

      const { access_token, token_type } = response.data;
      
      // Save token to localStorage
      localStorage.setItem('adminToken', access_token);
      
      // Fetch admin user data
      const adminResponse = await axios.get('/admin/me', {
        headers: {
          Authorization: `${token_type} ${access_token}`
        }
      });
      
      setAdmin(adminResponse.data);
      return true;
    } catch (err) {
      console.error('Admin login error:', err);
      setError(err.response?.data?.detail || 'Login failed. Please try again.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Admin logout function
  const logout = () => {
    localStorage.removeItem('adminToken');
    setAdmin(null);
  };

  const value = {
    admin,
    loading,
    error,
    login,
    logout,
    isAuthenticated: !!admin
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};